import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from '@lingui/macro';
import { Typography, styled } from '@mui/material';
import type { DropzoneOptions } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { Upload } from '@phosphor-icons/react';
import type { FileInputProps } from '../types';

import { Text } from 'components/text/Text';
import { FilePreview } from './FilePreview';
import { DOCUMENT_PREVIEW_HEIGHT_PX } from './filePreview.styles';
import { Button } from 'components/button/Button';

export const FileInput = ({ displayAs = 'input', text, sx, options }: FileInputProps) => {
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const previewUrl = useMemo(
    () => (previewFile ? URL.createObjectURL(previewFile) : undefined),
    [previewFile]
  );

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const mergedOptions: DropzoneOptions | undefined = useMemo(() => {
    if (displayAs !== 'preview') return options;
    return {
      ...options,
      onDrop: (accepted, fileRejections, evt) => {
        if (accepted[0]) {
          setPreviewFile(accepted[0]);
        }
        options?.onDrop?.(accepted, fileRejections, evt);
      },
    };
  }, [displayAs, options]);

  const { getRootProps, getInputProps } = useDropzone({ ...mergedOptions });

  const clearPreview = useCallback(() => {
    setPreviewFile(null);
  }, []);

  if (displayAs === 'area') {
    return (
      <Area sx={sx} {...getRootProps()}>
        <input {...getInputProps()} />
        <Typography variant="bodyMedium">
          <Trans>{text}</Trans>
        </Typography>
      </Area>
    );
  }

  if (displayAs === 'input') {
    return (
      <div {...getRootProps()}>
        <FileButton sx={sx} variant="secondary" startIcon={<Upload size={24} />}>
          <Typography variant="bodyMedium">{text}</Typography>
          <input {...getInputProps()} />
        </FileButton>
      </div>
    );
  }

  if (displayAs === 'preview') {
    return (
      <PreviewDropArea
        sx={[
          previewFile ? { minHeight: DOCUMENT_PREVIEW_HEIGHT_PX } : {},
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        {previewFile && previewUrl ? (
          <PreviewContentShell
            onClick={e => e.stopPropagation()}
            onPointerDown={e => e.stopPropagation()}
          >
            <FilePreview file={previewFile} previewUrl={previewUrl} />
          </PreviewContentShell>
        ) : (
          <Text variant="bodyMd" align="center">
            <Trans>{text}</Trans>
          </Text>
        )}
        {previewFile && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              clearPreview();
            }}
            style={{
              alignSelf: 'center',
              cursor: 'pointer',
              border: 'none',
              background: 'none',
              padding: 0,
              font: 'inherit',
            }}
          >
            <Text variant="labelSm">
              <Trans>Clear</Trans>
            </Text>
          </button>
        )}
      </PreviewDropArea>
    );
  }

  return null;
};

const Area = styled('div')(({ theme }) => ({
  borderStyle: 'dashed',
  borderWidth: '1px',
  padding: theme.spacing(3),
}));

const PreviewDropArea = styled('div')(({ theme }) => ({
  borderStyle: 'dashed',
  borderWidth: '1px',
  padding: theme.spacing(3),
  minHeight: 200,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

/** Flex child that fills the drop area; isolates preview from parent dropzone pointer/click handling. */
const PreviewContentShell = styled('div')({
  flex: '1 1 0%',
  minHeight: 0,
  minWidth: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
});

const FileButton = styled(Button)(({ theme }) => ({
  paddingBlock: theme.spacing(2),
  paddingInline: theme.spacing(3),
}));
